import { animate, state, style, transition, trigger } from '@angular/animations'
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { Router } from '@angular/router'

import { Lasku } from '@lemon/core-firebase/model/lasku'
import { LaskuToistoTyyppi, ToistuvaLasku } from '@lemon/core-firebase/model/lasku-toistuva'
import { LaskuLahetyksenToistoService } from '@lemon/core-firebase/service/lasku/lasku-lahetyksen-toisto.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { DateService } from '@lemon/core/service/date.service'

import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { firstValueFrom, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { FirebaseLemonaid } from '../_angular/service/firebase-lemonaid.service'
import { LaskuService } from '../_angular/service/lasku/lasku.service'
import { LaskuAjastaDialog, LaskuAjastaDialogData } from './dialogit/lasku-ajasta.dialog'

interface ToistuvaLaskuRivi extends ToistuvaLasku {
  avain: string
  asiakasNimi: string
  summa: number
  seuraavaLahetysPvmDate: Date
  lahetysFrekvenssi: string
  ensimmainenLaskuPvm: Date
  viimeinenLaskuPvm: Date | null
  yhteensaLahetyksia: number | null
  lahetetytLaskut?: Array<Pick<Lasku, 'avain' | 'p' | 'erap' | 'summa' | 'nro'>>
  ladataanLahetettyjaTietoja?: boolean
}

@Component({
  selector: 'app-toistuvat-laskut',
  templateUrl: './toistuvat-laskut.component.html',
  styleUrls: ['./toistuvat-laskut.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class ToistuvatlaskutComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator
  @ViewChild(MatSort) sort: MatSort

  private ngUnsubscribe = new Subject<void>()

  dataSource = new MatTableDataSource<ToistuvaLaskuRivi>([])
  displayedColumns: string[] = ['expand', 'asiakasNimi', 'lahetysFrekvenssi', 'ensimmainenLaskuPvm', 'viimeinenLaskuPvm', 'lahetyksia', 'summa', 'toiminnot']
  expandedElement: ToistuvaLaskuRivi | null = null
  readonly lataa = signal(true)

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _laskuService: LaskuService,
    private _dateService: DateService,
    private _dialog: MatDialog,
    private _router: Router,
    private _kayttajaService: KayttajaService,
    private _laskuUriService: LaskuUriService,
    private _translationService: LemonTranslationService,
    private _laskuLahetyksenToistoService: LaskuLahetyksenToistoService,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.haeToistuvatLaskut()
  }
  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  trackByAvain(index: number, rivi: ToistuvaLaskuRivi): string {
    return rivi.avain
  }

  async haeToistuvatLaskut() {
    const asiakas = await firstValueFrom(this._kayttajaService.nykyinenAsiakasObservable)
    const toistuvaLaskuUri = this._laskuUriService.getToistuvatLaskutCollectionUri(asiakas.asiakasAvain)

    this._firebaseLemonaid
      .firestoreCollection<ToistuvaLasku>(toistuvaLaskuUri)
      .listenSnapshots()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: snapshots => {
          const rivit: ToistuvaLaskuRivi[] = []
          for (const snapshot of snapshots) {
            const toistuva = snapshot.data() as ToistuvaLasku
            if (!toistuva.poistettu) {
              rivit.push({
                ...toistuva,
                avain: snapshot.id,
                asiakasNimi: toistuva.lasku.asiakas?.nimi || '',
                summa: toistuva.lasku.summa || 0,
                seuraavaLahetysPvmDate: this._dateService.numberToDate(toistuva.seuraavaLahetysPvm),
                lahetysFrekvenssi: this.muotoileLahetysFrekvenssi(toistuva.toisto.toistoTyyppi),
                ensimmainenLaskuPvm: this._dateService.numberToDate(toistuva.toisto.ensimmainenLahetyspvm),
                // Viimeisen laskun pvm ( "-" jos vain yksi kerta)
                viimeinenLaskuPvm: this._laskuLahetyksenToistoService.annaViimeinenLaskupvmJosLaskusarjassa(toistuva.toisto, toistuva.lahetyksia),
                yhteensaLahetyksia: this._laskuLahetyksenToistoService.laskeLahetyksienKokonaismaara(toistuva.toisto),
              })
            }
          }

          this.dataSource.data = rivit
          this.lataa.set(false)

          setTimeout(() => {
            if (this.paginator && this.sort) {
              this.dataSource.paginator = this.paginator
              this.dataSource.sort = this.sort
              // Oletussorttaus
              if (!this.sort.active) {
                this.sort.active = 'ensimmainenLaskuPvm'
                this.sort.direction = 'desc'
                this.dataSource.sort = this.sort
              }
            }
          }, 0)
        },
      })
  }

  async poista(rivi: ToistuvaLaskuRivi) {
    const dialogData: AreYouSureDialogData = {
      header: this._translationService.lokalisoi('lasku.ajasta-dialog.poista-ajastus.otsikko'),
      text: this._translationService.lokalisoi('lasku.ajasta-dialog.poista-ajastus.teksti'),
      rightAction: this._translationService.lokalisoi('lasku.ajasta-dialog.poista-ajastus.poista'),
    }

    const isSure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: dialogData }).afterClosed())

    if (isSure) {
      await this._laskuService.poistaToistuvaLasku(rivi)
    }
  }

  katseleLaskua(rivi: ToistuvaLaskuRivi) {
    this._router.navigate(['/laskutus/laskut', rivi.lasku.avain, rivi.lasku.avain, 'katsele'], {
      queryParams: { paluu: 'toistuvat' },
    })
  }

  katseleLahetettyaLaskua(rivi: ToistuvaLaskuRivi, laskuAvain: string) {
    this._router.navigate(['/laskutus/laskut', laskuAvain, laskuAvain, 'katsele'], {
      queryParams: { paluu: 'toistuvat' },
    })
  }

  async toggleRow(rivi: ToistuvaLaskuRivi) {
    if (this.expandedElement === rivi) {
      this.expandedElement = null
      return
    }

    this.expandedElement = rivi

    if (!rivi.lahetetytLaskut) {
      rivi.ladataanLahetettyjaTietoja = true
      this.dataSource.data = [...this.dataSource.data]

      try {
        const asiakas = await firstValueFrom(this._kayttajaService.nykyinenAsiakasObservable)
        const laskutUri = this._laskuUriService.getCustomerLaskutCollectionUri(asiakas.asiakasAvain)

        const laskut = await this._firebaseLemonaid.firestoreCollection<Lasku>(laskutUri).where('toistoasetustenAvain', '==', rivi.avain).get()

        rivi.lahetetytLaskut = laskut
          .map(lasku => ({
            avain: lasku.avain,
            nro: lasku.nro,
            p: lasku.p,
            erap: lasku.erap,
            summa: lasku.summa,
          }))
          .sort((a, b) => b.p - a.p)
      } catch (error) {
        console.error('Virhe haettaessa lähetettyjä laskuja:', error)
        rivi.lahetetytLaskut = []
      } finally {
        rivi.ladataanLahetettyjaTietoja = false
        this.dataSource.data = [...this.dataSource.data]
        this._changeDetectorRef.markForCheck()
      }
    }
  }

  private muotoileLahetysFrekvenssi(toistoTyyppi: LaskuToistoTyyppi): string {
    switch (toistoTyyppi) {
      case 'kerran':
        return this._translationService.lokalisoi('toistuvat.frekvenssi.kerran')
      case 'kk':
        return this._translationService.lokalisoi('toistuvat.frekvenssi.kuukausittain')
      case 'kk3':
        return this._translationService.lokalisoi('toistuvat.frekvenssi.3kk-valein')
      case 'kk6':
        return this._translationService.lokalisoi('toistuvat.frekvenssi.6kk-valein')
      case 'vuosi':
        return this._translationService.lokalisoi('toistuvat.frekvenssi.vuosittain')
      default:
        return toistoTyyppi
    }
  }

  naytaMuokkausDialogi(rivi: ToistuvaLaskuRivi) {
    const dialogData: LaskuAjastaDialogData = {
      lasku: rivi.lasku,
      nykyisetAsetukset: rivi.toisto,
    }
    this._dialog.open(LaskuAjastaDialog, { data: dialogData })
  }
}
